/**
 * Ephemeral + server-backed share canvas comments (Figma-style pins).
 * Server lists may load whenever the canvas/share page is open; polling follows comment mode.
 * Pins render only when `commentPlacementMode` is on.
 */

import { create } from "zustand";
import {
  shareCommentAuthorLabelFromEmail,
  shareCommentAuthorSeedFromEmail,
} from "@/lib/shareComments/author";

export type ShareCommentRecord = {
  id: string;
  parentId: string | null;
  authorLabel: string;
  authorSeed: string;
  text: string;
  canvasX: number;
  canvasY: number;
  hidden: boolean;
  createdAt: string;
  /** True only for viewer-owned threads on public /p/[token] responses. */
  viewerCanModerate: boolean;
};

type ShareCommentState = {
  comments: ShareCommentRecord[];
  /** When false, pins are hidden and canvas placement is off (viewer + owner). */
  commentPlacementMode: boolean;
  viewerEmail: string | null;
  setComments: (comments: ShareCommentRecord[]) => void;
  addComment: (c: ShareCommentRecord) => void;
  removeComment: (id: string) => void;
  resolveCommentId: (tempId: string, realId: string) => void;
  setCommentHidden: (id: string, hidden: boolean) => void;
  /** Root archived/reopened: sync hidden on direct replies (client mirror of server cascade). */
  setCommentThreadHidden: (rootId: string, hidden: boolean) => void;
  toggleCommentPlacementMode: () => void;
  setCommentPlacementMode: (on: boolean) => void;
  setViewerEmail: (email: string | null) => void;
  /** Clear viewer session state when opening a share link (avoid cross-tab leakage). */
  resetViewerSession: () => void;
  /** Owner leaves canvas — drop comment list so next project does not flash stale pins. */
  resetOwnerComments: () => void;
  /** Move root + all replies to new flow coordinates (after successful relocate). */
  relocateCommentThread: (rootId: string, canvasX: number, canvasY: number) => void;
  /** While non-null, thread popovers for that root should close (drag in progress). */
  draggingShareCommentRootId: string | null;
  setDraggingShareCommentRootId: (id: string | null) => void;
  /** Which root thread popover is open (only one at a time across the canvas). */
  openShareCommentThreadRootId: string | null;
  setOpenShareCommentThreadRootId: (rootId: string | null) => void;
  /** Which root is showing the hover preview (raises that pin above siblings). */
  hoverPreviewShareCommentRootId: string | null;
  setHoverPreviewShareCommentRootId: (rootId: string | null) => void;
};

function toRecord(row: {
  id: string;
  parentId?: string | null;
  email?: string | null;
  authorLabel?: string;
  authorSeed?: string;
  /**
   * Server-side join from member-scoped procedures
   * (`shareCommentOwnerSelect.authorUser`). Lets us render the real
   * Clerk display name on the canvas thread instead of falling through
   * to an email-local-part transform for authenticated authors.
   * (LEV-659 QA follow-up.)
   */
  authorUser?: { name?: string | null } | null;
  text: string;
  canvasX: number | null;
  canvasY: number | null;
  hidden: boolean;
  viewerCanModerate?: boolean;
  createdAt: Date | string;
}): ShareCommentRecord {
  const fallbackEmail = row.email?.trim() ?? "";
  const userName = row.authorUser?.name?.trim() || "";
  const authorLabel =
    row.authorLabel ??
    (userName ||
      (fallbackEmail
        ? shareCommentAuthorLabelFromEmail(fallbackEmail)
        : "Unknown"));
  const authorSeed =
    row.authorSeed ??
    (fallbackEmail
      ? shareCommentAuthorSeedFromEmail(fallbackEmail)
      : shareCommentAuthorSeedFromEmail(authorLabel));
  return {
    id: row.id,
    parentId: row.parentId ?? null,
    authorLabel,
    authorSeed,
    text: row.text,
    canvasX: row.canvasX ?? 0,
    canvasY: row.canvasY ?? 0,
    hidden: row.hidden,
    viewerCanModerate: row.viewerCanModerate ?? false,
    createdAt:
      typeof row.createdAt === "string" ? row.createdAt : row.createdAt.toISOString(),
  };
}

export const useShareCommentStore = create<ShareCommentState>((set) => ({
  comments: [],
  commentPlacementMode: false,
  viewerEmail: null,
  draggingShareCommentRootId: null,
  openShareCommentThreadRootId: null,
  hoverPreviewShareCommentRootId: null,

  setComments: (incoming) =>
    set((s) => {
      const previousById = new Map(s.comments.map((c) => [c.id, c]));
      const normalizedIncoming = incoming.map((row) => {
        const previous = previousById.get(row.id);
        if (previous?.viewerCanModerate && !row.viewerCanModerate) {
          return { ...row, viewerCanModerate: true };
        }
        return row;
      });
      const optimistic = s.comments.filter(
        (c) => c.id.startsWith("temp-") || c.parentId?.startsWith("temp-"),
      );
      const incomingIds = new Set(normalizedIncoming.map((c) => c.id));
      const kept = optimistic.filter((o) => !incomingIds.has(o.id));
      if (kept.length > 0) {
        console.info("[ShareComments] poll merge: preserving optimistic", kept.map((c) => c.id));
      }
      const merged =
        kept.length > 0 ? [...normalizedIncoming, ...kept] : normalizedIncoming;
      const openId = s.openShareCommentThreadRootId;
      if (!openId) return { comments: merged };
      const stillExists = merged.some((c) => c.id === openId && c.parentId === null);
      return {
        comments: merged,
        ...(stillExists ? {} : { openShareCommentThreadRootId: null }),
      };
    }),

  setDraggingShareCommentRootId: (id) =>
    set((s) => ({
      draggingShareCommentRootId: id,
      ...(id
        ? {
            openShareCommentThreadRootId: null,
            hoverPreviewShareCommentRootId: null,
          }
        : {}),
    })),

  setOpenShareCommentThreadRootId: (rootId) =>
    set({ openShareCommentThreadRootId: rootId }),

  setHoverPreviewShareCommentRootId: (rootId) =>
    set({ hoverPreviewShareCommentRootId: rootId }),

  relocateCommentThread: (rootId, canvasX, canvasY) =>
    set((s) => ({
      comments: s.comments.map((c) =>
        c.id === rootId || c.parentId === rootId ? { ...c, canvasX, canvasY } : c,
      ),
    })),

  addComment: (c) =>
    set((s) => ({
      comments: [...s.comments, c],
    })),

  removeComment: (id) =>
    set((s) => ({
      comments: s.comments.filter((c) => c.id !== id && c.parentId !== id),
      ...(s.openShareCommentThreadRootId === id
        ? { openShareCommentThreadRootId: null }
        : {}),
    })),

  resolveCommentId: (tempId, realId) =>
    set((s) => {
      // Swap temp→real, then deduplicate (poll may have already added the real entry)
      const resolved = s.comments.map((c) => {
        if (c.id === tempId) return { ...c, id: realId };
        if (c.parentId === tempId) return { ...c, parentId: realId };
        return c;
      });
      const seen = new Set<string>();
      const deduped = resolved.filter((c) => {
        if (seen.has(c.id)) return false;
        seen.add(c.id);
        return true;
      });
      return {
        comments: deduped,
        ...(s.openShareCommentThreadRootId === tempId
          ? { openShareCommentThreadRootId: realId }
          : {}),
      };
    }),

  setCommentHidden: (id, hidden) =>
    set((s) => ({
      comments: s.comments.map((c) => (c.id === id ? { ...c, hidden } : c)),
    })),

  setCommentThreadHidden: (rootId, hidden) =>
    set((s) => ({
      comments: s.comments.map((c) =>
        c.id === rootId || c.parentId === rootId ? { ...c, hidden } : c,
      ),
    })),

  toggleCommentPlacementMode: () =>
    set((s) => {
      const next = !s.commentPlacementMode;
      return {
        commentPlacementMode: next,
        ...(!next
          ? { openShareCommentThreadRootId: null, hoverPreviewShareCommentRootId: null }
          : {}),
      };
    }),

  setCommentPlacementMode: (on) =>
    set((s) => ({
      commentPlacementMode: on,
      ...(!on
        ? { openShareCommentThreadRootId: null, hoverPreviewShareCommentRootId: null }
        : {}),
    })),

  setViewerEmail: (email) => set({ viewerEmail: email }),

  resetViewerSession: () =>
    set({
      comments: [],
      viewerEmail: null,
      commentPlacementMode: false,
      draggingShareCommentRootId: null,
      openShareCommentThreadRootId: null,
      hoverPreviewShareCommentRootId: null,
    }),

  resetOwnerComments: () =>
    set({
      comments: [],
      commentPlacementMode: false,
      draggingShareCommentRootId: null,
      openShareCommentThreadRootId: null,
      hoverPreviewShareCommentRootId: null,
    }),
}));

export function shareCommentsFromServer(
  rows: Array<{
    id: string;
    parentId?: string | null;
    email?: string | null;
    authorLabel?: string;
    authorSeed?: string;
    /** Owner/member rows join the author's User profile (LEV-941). */
    authorUser?: { name?: string | null } | null;
    text: string;
    canvasX: number | null;
    canvasY: number | null;
    hidden: boolean;
    viewerCanModerate?: boolean;
    createdAt: Date | string;
  }>,
): ShareCommentRecord[] {
  return rows.map(toRecord);
}
